import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

const flattenErrorKeys = (errors: unknown, prefix = ''): string[] => {
  if (!errors) return [];
  if (typeof errors === 'string') return prefix ? [prefix] : [];
  if (Array.isArray(errors)) {
    return errors.flatMap((item, i) =>
      flattenErrorKeys(item, prefix ? `${prefix}.${i}` : `${i}`),
    );
  }
  if (typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(
      ([key, val]) => flattenErrorKeys(val, prefix ? `${prefix}.${key}` : key),
    );
  }
  return [];
};

const findErrorElement = (key: string): HTMLElement | null =>
  (document.querySelector(`[data-formik-field="${key}"]`) ||
    document.querySelector(
      `[name="${CSS.escape(key)}"]`,
    )) as HTMLElement | null;

function ScrollToFirstFormikError() {
  const { isSubmitting, errors } = useFormikContext();
  const wasSubmitting = useRef(false);

  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting) {
      const elements = flattenErrorKeys(errors)
        .map(findErrorElement)
        .filter((el): el is HTMLElement => !!el);
      if (elements.length > 0) {
        const firstEl = elements.reduce((top, el) =>
          el.getBoundingClientRect().top < top.getBoundingClientRect().top
            ? el
            : top,
        );
        firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof firstEl.focus === 'function') {
          firstEl.focus({ preventScroll: true });
        }
      }
    }
    wasSubmitting.current = isSubmitting;
  }, [isSubmitting, errors]);

  return null;
}

export default ScrollToFirstFormikError;
